import { useEffect, useRef, useState } from 'react'
import {
  GREETING_MAX,
  SHARE_MODES,
  SHARE_MODE_INFO,
  VISIBILITY_LABEL,
  type BoardAccessSettingsDto,
  type BoardVisibility,
  type ShareLinkDto,
  type ShareMode,
  type WaitingRoomDto,
} from '@praesto/shared'
import { api, ApiError } from '../api.js'
import { Dialog } from './Dialog.js'
import { useText } from '../i18n.js'
import { WaitingScreen } from '../presentation/WaitingScreen.js'

type Tab = 'team' | 'guests'

/** Wie lange nach dem letzten Tastendruck gewartet wird, bevor gespeichert wird. */
const GREETING_SAVE_MS = 800

/**
 * Freigabe in zwei Schritten, die nichts miteinander zu tun haben: Wer im
 * eigenen Haus mitarbeiten darf — und wer von außen zuschauen darf.
 *
 * Beides in eine Liste zu werfen war der Grund, warum das Menü überladen wirkte:
 * Ein Kollege bekommt Bearbeitungsrechte, ein Gast bekommt einen Link. Getrennt
 * muss niemand erst lesen, um das Richtige zu finden.
 */
export function ShareDialog({
  boardId,
  canManage,
  onClose,
}: {
  boardId: string
  /** Nur Eigentümer und Verwaltung dürfen den Zugriff im Team ändern. */
  canManage: boolean
  onClose: () => void
}): React.ReactElement {
  const t = useText()
  const [tab, setTab] = useState<Tab>('team')
  const [access, setAccess] = useState<BoardAccessSettingsDto | null>(null)
  const [accessError, setAccessError] = useState<string | null>(null)
  const [links, setLinks] = useState<ShareLinkDto[] | null>(null)
  const [mode, setMode] = useState<ShareMode>('guided')
  const [greeting, setGreeting] = useState('')
  const [copied, setCopied] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [waiting, setWaiting] = useState<WaitingRoomDto | null>(null)
  const [showWaiting, setShowWaiting] = useState(false)
  const greetingTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const pendingGreetings = useRef(new Map<string, string>())

  // Wer den Dialog schließt, während die Verzögerung noch läuft, hat den Satz
  // trotzdem geschrieben — also geht er jetzt hinaus.
  useEffect(() => {
    const timers = greetingTimers.current
    const open = pendingGreetings.current
    return () => {
      for (const timer of timers.values()) clearTimeout(timer)
      timers.clear()
      for (const [token, text] of open) void api.setShareGreeting(token, text).catch(() => {})
      open.clear()
    }
  }, [])

  useEffect(() => {
    api
      .shares(boardId)
      .then(setLinks)
      .catch(() => setLinks([]))
    api
      .boardAccess(boardId)
      .then(setAccess)
      .catch(() => setAccessError(t('share.accessLoadFailed')))
    // Der Auftritt für die Vorschau. Scheitert das, entfällt nur die Vorschau.
    api
      .waitingRoom(boardId)
      .then(setWaiting)
      .catch(() => setWaiting(null))
  }, [boardId])

  async function setVisibility(visibility: BoardVisibility): Promise<void> {
    setAccessError(null)
    const previous = access
    setAccess((current) => (current ? { ...current, visibility } : current))
    try {
      await api.setBoardVisibility(boardId, visibility)
    } catch (caught) {
      setAccess(previous)
      setAccessError(caught instanceof ApiError ? caught.message : t('share.changeFailed'))
    }
  }

  async function grant(userId: string): Promise<void> {
    await api.addBoardMember(boardId, userId)
    setAccess((current) => {
      if (!current) return current
      const person = current.candidates.find((c) => c.id === userId)
      if (!person) return current
      return {
        ...current,
        members: [...current.members, person].sort((a, b) =>
          a.displayName.localeCompare(b.displayName, 'de'),
        ),
        candidates: current.candidates.filter((c) => c.id !== userId),
      }
    })
  }

  async function withdraw(userId: string): Promise<void> {
    await api.removeBoardMember(boardId, userId)
    setAccess((current) => {
      if (!current) return current
      const person = current.members.find((m) => m.id === userId)
      if (!person) return current
      return {
        ...current,
        members: current.members.filter((m) => m.id !== userId),
        candidates: [...current.candidates, person].sort((a, b) =>
          a.displayName.localeCompare(b.displayName, 'de'),
        ),
      }
    })
  }

  async function create(): Promise<void> {
    setBusy(true)
    try {
      const link = await api.createShare(boardId, mode, greeting.trim())
      setLinks((current) => [link, ...(current ?? [])])
      setGreeting('')
    } finally {
      setBusy(false)
    }
  }

  async function revoke(token: string): Promise<void> {
    await api.revokeShare(token)
    setLinks((current) => (current ?? []).filter((link) => link.token !== token))
  }

  /**
   * Begrüßung eines schon vergebenen Links ändern.
   *
   * Verzögert geschrieben, wie die Sprechernotizen: Je Tastendruck eine Anfrage
   * hieße für einen Satz zweihundert Schreibvorgänge — und die zuletzt
   * abgeschickte wäre nicht zwingend die zuletzt angekommene.
   */
  function changeGreeting(token: string, text: string): void {
    setLinks((current) =>
      (current ?? []).map((link) => (link.token === token ? { ...link, greeting: text } : link)),
    )
    pendingGreetings.current.set(token, text)

    const running = greetingTimers.current.get(token)
    if (running) clearTimeout(running)
    greetingTimers.current.set(
      token,
      setTimeout(() => {
        greetingTimers.current.delete(token)
        pendingGreetings.current.delete(token)
        void api.setShareGreeting(token, text).catch(() => {
          // Beim nächsten Öffnen des Dialogs steht wieder der gespeicherte Stand.
        })
      }, GREETING_SAVE_MS),
    )
  }

  async function copy(url: string): Promise<void> {
    await navigator.clipboard.writeText(url)
    setCopied(url)
    setTimeout(() => setCopied(null), 1800)
  }

  return (
    <Dialog title={t('board.share')} onClose={onClose}>
      <div className="tab-row" role="tablist">
        <button
          role="tab"
          aria-selected={tab === 'team'}
          className={tab === 'team' ? 'tab is-active' : 'tab'}
          onClick={() => setTab('team')}
        >
          {t('share.tabTeam')}
        </button>
        <button
          role="tab"
          aria-selected={tab === 'guests'}
          className={tab === 'guests' ? 'tab is-active' : 'tab'}
          onClick={() => setTab('guests')}
        >
          {t('share.tabGuests')}
        </button>
      </div>

      {tab === 'team' && (
        <>
          <p className="muted small">{t('share.teamLead')}</p>

          {access === null && !accessError && <p className="muted small">{t('common.loading')}</p>}
          {access && (
            <>
              <fieldset className="mode-choice" disabled={!canManage}>
                <legend className="sr-only">{t('share.visibility')}</legend>
                {(['private', 'organisation'] as const).map((option) => (
                  <label
                    key={option}
                    className={access.visibility === option ? 'is-selected' : undefined}
                  >
                    <input
                      type="radio"
                      name="visibility"
                      value={option}
                      checked={access.visibility === option}
                      onChange={() => void setVisibility(option)}
                    />
                    <span className="mode-body">
                      <span className="mode-label">{t(VISIBILITY_LABEL[option])}</span>
                      <span className="mode-description">
                        {option === 'private'
                          ? t('share.privateHint')
                          : t('share.orgHint')}
                      </span>
                    </span>
                  </label>
                ))}
              </fieldset>

              <ul className="member-list">
                <li>
                  <span className="member-name">{access.owner.displayName}</span>
                  <span className="member-mail muted small">{access.owner.email}</span>
                  <span className="badge subtle">{t('share.owner')}</span>
                </li>
                {access.members.map((member) => (
                  <li key={member.id}>
                    <span className="member-name">{member.displayName}</span>
                    <span className="member-mail muted small">{member.email}</span>
                    {canManage && (
                      <button
                        className="button ghost icon"
                        onClick={() => void withdraw(member.id)}
                        aria-label={t('share.withdrawFor', { name: member.displayName })}
                        title={t('share.withdraw')}
                      >
                        ×
                      </button>
                    )}
                  </li>
                ))}
              </ul>

              {canManage && access.candidates.length > 0 && (
                <label className="field">
                  <span>{t('share.addPerson')}</span>
                  <select
                    value=""
                    onChange={(event) => {
                      if (event.target.value) void grant(event.target.value)
                    }}
                  >
                    <option value="">{t('share.choosePerson')}</option>
                    {access.candidates.map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.displayName}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {access.visibility === 'organisation' && (
                <p className="muted small">{t('share.orgOpenHint')}</p>
              )}
              {!canManage && (
                <p className="muted small">{t('share.ownerOnly')}</p>
              )}
            </>
          )}
          {accessError && <p className="form-error">{accessError}</p>}
        </>
      )}

      {tab === 'guests' && (
        <>
      <p className="muted small">{t('share.guestLead')}</p>

      <fieldset className="mode-choice">
        <legend className="sr-only">{t('share.mode')}</legend>
        {SHARE_MODES.map((candidate) => {
          const info = SHARE_MODE_INFO[candidate]
          return (
            <label key={candidate} className={mode === candidate ? 'is-selected' : undefined}>
              <input
                type="radio"
                name="share-mode"
                value={candidate}
                checked={mode === candidate}
                onChange={() => setMode(candidate)}
              />
              <span className="mode-body">
                <span className="mode-label">{t(info.label)}</span>
                <span className="mode-description">{t(info.description)}</span>
              </span>
            </label>
          )
        })}
      </fieldset>

      {/*
        Die Begrüßung hängt am Link, nicht am Board: Dasselbe Board geht morgens
        an den Kunden und nachmittags ins eigene Haus, und beide sollen nicht
        denselben Satz lesen.
      */}
      <label className="field">
        <span>
          {t('share.greeting')} <span className="muted small">{t('share.optional')}</span>
        </span>
        <input
          value={greeting}
          maxLength={GREETING_MAX}
          onChange={(event) => setGreeting(event.target.value)}
          placeholder={t('share.greetingPlaceholder')}
        />
      </label>
      {mode === 'explore' && greeting.trim() !== '' && (
        <p className="muted small">{t('share.noWaitingHint')}</p>
      )}

      <div className="button-row">
        <button className="button primary" onClick={create} disabled={busy}>
          {busy ? t('share.creating') : t('share.createLink')}
        </button>
        {waiting && (
          <button className="button ghost" onClick={() => setShowWaiting(true)}>
            {t('share.previewWaiting')}
          </button>
        )}
      </div>

      {links?.length === 0 && <p className="muted small">{t('share.noLinks')}</p>}

      <ul className="link-list">
        {links?.map((link) => (
          <li key={link.token}>
            <span className={`mode-badge mode-${link.mode}`}>
              {t(SHARE_MODE_INFO[link.mode].label)}
            </span>
            <input readOnly value={link.url} onFocus={(event) => event.target.select()} />
            <button
              className="button ghost"
              style={{ gridArea: 'copy' }}
              onClick={() => copy(link.url)}
            >
              {copied === link.url ? t('common.copied') : t('common.copy')}
            </button>
            <button
              className="button ghost danger"
              style={{ gridArea: 'revoke' }}
              onClick={() => revoke(link.token)}
            >
              {t('share.revoke')}
            </button>
            {SHARE_MODE_INFO[link.mode].contentBeforePresentation ? null : (
              <input
                className="link-greeting"
                value={link.greeting}
                maxLength={GREETING_MAX}
                placeholder={t('share.greeting')}
                onChange={(event) => changeGreeting(link.token, event.target.value)}
              />
            )}
          </li>
        ))}
      </ul>
        </>
      )}

      {/* Die Vorschau ist der echte Wartebildschirm, nicht sein Abbild — mit
          dem Text, der gerade im Feld steht. */}
      {showWaiting && waiting && (
        <div className="waiting-preview" onClick={() => setShowWaiting(false)}>
          <WaitingScreen
            boardTitle={waiting.boardTitle}
            mode={mode}
            waiting={{ ...waiting, greeting: greeting.trim() }}
          />
          <button className="button ghost waiting-preview-close">{t('share.closePreview')}</button>
        </div>
      )}
    </Dialog>
  )
}
